<template>
  <div class="signup-form vuejs-signup-form">
    <form-component
      class="signup-form__form"
      :modifiers="['full-width-xs']"
      action="/nc/signup"
      method="post">
      <input
        v-if="lastPageBeforeLoginValue"
        type="hidden"
        name="last_page_before_login"
        :value="lastPageBeforeLoginValue">

      <input
        type="hidden"
        name="authenticity_token"
        :value="authenticityToken">

      <input
        type="hidden"
        name="signup[facebook_access_token]"
        class="js-signup-form-fb-access-token"
        value="">

      <input
        type="hidden"
        name="signup[google_id_token]"
        class="js-google-id-token"
        value="">

      <input
        type="hidden"
        name="signup[form_id]"
        :value="formId">

      <input
        v-for="field in hiddenFields"
        :key="field.name + '-' + field.value"
        type="hidden"
        :name="field.name"
        :value="field.value">

      <input-text
        class="signup-form__input"
        name="signup[email]"
        :required="true"
        :placeholder="emailCopy"
        v-model="email" />

      <input-text
        class="signup-form__input"
        name="signup[password]"
        :required="true"
        :type="'password'"
        :minlength="8"
        :placeholder="$t('password')"
        v-model="password" />

      <button-vue
        class="signup-form__cta"
        ref="createAccountButton"
        :re-clickable="false"
        :modifiers="['full']"
        :copy="$t('cta')"
        :disabled="disabled" />

      <div v-if="!simplifiedForm">
        <div class="signup-form__separator">
          <span>{{ $t("or") }}</span>
        </div>

        <button-vue
          class="mb-4 signup-form__facebook-button"
          :modifiers="['facebook', 'full', 'icon-32']"
          :disabled="disabled"
          data-action="facebook-login"
          data-action-url=".js-signup-form-fb-access-token">
          <img
            :src="$options.fbIcon"
            loading="lazy"
            alt="Facebook Icon">
          <span v-html="$t('facebookCta')" />
        </button-vue>
      </div>

      <button-vue
        v-if="showGoogleButton"
        type="button"
        class="mb-4 signup-form__google-button js-google-signin"
        :modifiers="['google', 'full', 'icon-32']"
        :disabled="disabled">
        <img
          :src="$options.googleIcon"
          loading="lazy"
          alt="Google Icon">
        <span v-html="$t('googleCta')" />
      </button-vue>

      <apple-login-button
        v-if="showAppleButton"
        class="mb-4"
        :disabled="disabled"
        :last-page-before-login-value="lastPageBeforeLoginValue"
        :user-data-json="userDataJsonParams" />

      <div
        :class="`mt-4 signup-form__legal ${legalTextAlignment}`"
        v-html="$t('legal')" />
    </form-component>
  </div>
</template>

<script>
import FormComponent from "@/shared/form/form";
import InputText from "@/shared/form/input-text";
import ButtonVue from "@/shared/button";
import AppleLoginButton from "@/component-library/apple-login-button/index";
import { mapGetters } from "vuex";
import events from "@/events";
import fbIcon from "./images/facebook-icon.svg";
import googleIcon from "./images/google-icon.svg";
import UAParser from "ua-parser-js";

export default {
  name: "SignupForm",
  components: {
    FormComponent,
    InputText,
    ButtonVue,
    AppleLoginButton
  },
  props: {
    formId: {
      type: String,
      default: "body-20210521-generic_signup_form"
    },
    hiddenFields: {
      type: Array,
      default: () => []
    },
    lastPageBeforeLogin: {
      type: String,
      default: ""
    },
    booksToAdd: {
      type: Array,
      default: () => []
    },
    legalTextAlignment: {
      type: String,
      default: "text-center"
    },
    simplifiedForm: {
      type: Boolean,
      default: false
    },
    b2bForm: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      email: null,
      password: null
    };
  },
  fbIcon,
  googleIcon,
  computed: {
    showGoogleButton() {
      if (this.simplifiedForm) {
        return false;
      }

      const osName = new UAParser().getResult().os.name;
      if (!osName) {
        return false;
      }

      return osName.toLocaleLowerCase().includes("android");
    },
    showAppleButton() {
      if (this.simplifiedForm) {
        return false;
      }

      const osName = new UAParser().getResult().os.name;
      if (!osName) {
        return false;
      }

      return osName.toLocaleLowerCase().includes("ios");
    },
    disabled() {
      return !this.authenticityToken;
    },
    userDataJsonParams() {
      const data = JSON.parse(this.userDataJson);
      data["books_to_add"] = [...this.booksToAdd];
      data["categories_to_follow"] = [...this.selectedTopics];
      return JSON.stringify(data);
    },
    lastPageBeforeLoginValue() {
      if (this.lastPageBeforeLogin) {
        return this.lastPageBeforeLogin;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const lastPageBeforeLogin = urlParams.get("last_page_before_login");

      return lastPageBeforeLogin;
    },
    emailCopy() {
      if (this.b2bForm) {
        return this.$t("workEmail");
      } else {
        return this.$t("email");
      }
    },
    ...mapGetters(["authenticityToken"]),
    ...mapGetters("onboarding", ["userDataJson", "selectedTopics"])
  },
  watch: {
    email() {
      this.reEnableCreateAccountButton();
    },
    password() {
      this.reEnableCreateAccountButton();
    }
  },
  mounted() {
    events.emit("facebook:init");
    events.emit("google:init");
  },
  methods: {
    reEnableCreateAccountButton() {
      // This is used to make the button enabled again after the user made changes to the form input.
      this.$refs.createAccountButton.resetClickCounter();
    }
  },
  i18n: {
    messages: {
      en: {
        email: "Email address",
        workEmail: "Work email address",
        password: "Create password",
        cta: "Create account",
        or: "or",
        facebookCta: "Sign up with Facebook",
        googleCta: "Sign up with Google",
        legal: "By continuing you indicate that you've read and agree to our <a target='_blank' href='/tos'>Terms of Service</a> and <a target='_blank' href='/privacy'>Privacy Policy</a>."
      },
      de: {
        email: "E-Mail Adresse",
        password: "Passwort erstellen",
        cta: "Konto erstellen",
        or: "oder",
        facebookCta: "Mit Facebook registrieren",
        googleCta: "Mit Google anmelden",
        legal: "Mit der Anmeldung stimme ich den <a target='_blank' href='/de/tos'>AGB</a> und <a target='_blank' href='/de/privacy'>Datenschutzrichtlinien</a> zu."
      }
    }
  }
};
</script>

<style lang="scss">
.signup-form {
  @include font-family(sans-serif);

  &__form {
    max-width: 100%;

    @include media(min-width, m) {
      max-width: inherit;
    }
  }

  &__email-input-label {
    @include font-style(h5, $color: $mid-grey);
  }

  &__input {
    margin-bottom: $spacing-16;
  }

  &__separator {
    width: 100%;
    margin: $spacing-32 0 $spacing-32;  /* stylelint-disable-line unit-blacklist */

    color: $mid-grey;
    font-size: spacing(14px); /* stylelint-disable-line unit-blacklist */
    line-height: spacing(1px);
    text-align: center;

    border-bottom: spacing(1px) solid $light-grey;

    span {  /* stylelint-disable-line selector-nested-pattern */
      @include font-style(caption, $weight: medium, $color: $mid-grey, $line-height: spacing(1px));

      padding: 0 $spacing-16; /* stylelint-disable-line unit-blacklist */

      background-color: $white;
    }
  }

  &__facebook-button {
    position: relative;

    img { // stylelint-disable-line selector-nested-pattern
      position: absolute;
      top: 50%;
      left: $spacing-16;

      height: $spacing-24;

      transform: translateY(-50%);
    }

    span { // stylelint-disable-line selector-nested-pattern
      margin-left: $spacing-16;

      @include media(min-width, s) {
        margin-left: 0;
      }
    }
  }

  &__google-button {
    position: relative;

    background-color: #4285f4;

    &:hover {
      background-color: #3367d6;
    }

    img { // stylelint-disable-line selector-nested-pattern
      position: absolute;
      top: 50%;
      left: rem(1px); // stylelint-disable-line unit-blacklist

      height: rem(40px);

      transform: translateY(-50%);
    }

    span { // stylelint-disable-line selector-nested-pattern
      margin-left: $spacing-16;

      @include media(min-width, s) {
        margin-left: 0;
      }
    }
  }

  &__legal {
    @include font-style(legal);

    a { /* stylelint-disable-line selector-nested-pattern */
      @include font-style(legal);

      text-decoration: underline;
    }
  }
}
</style>
